import styled from "styled-components";
import { largeComponentHeight } from "common/const/const";
import React, { useRef, useState } from "react";

import {
  inputBorderStyle,
  InputWrapper,
} from "common/components/Wrapper/InputBase";
import { useValidCheckSingle } from "common/hooks/validCheck";

const TIME_PATTERN = /^([01]\d|2[0-3]):[0-5]\d$/;

const TimeInputWrapper = styled.div`
  position: relative;
  display: flex;
  align-items: center;
  width: 100%;
  height: ${largeComponentHeight};
  text-align: center;
  ${inputBorderStyle}
  padding-left: ${(props) =>
    props.iconPosition === "left" ? "3.5rem" : "1.25rem"};
  padding-right: ${(props) =>
    props.iconPosition === "right" ? "3.5rem" : "1.25rem"};

  ${({ $borderless, $error }) => $borderless && !$error && `border: none;`}

  &:read-only {
    background-color: var(--White);
  }

  ${({ $disabled }) =>
    $disabled &&
    `
    background-color: var(--LightGray) !important;
    cursor: not-allowed;
  `}
`;

// 시/분 각각의 세그먼트 입력. 세그먼트를 나누면 네이티브로 "HH:MM" 전체를 가로질러
// 선택/복사할 수 없으므로, 복사/붙여넣기는 wrapper의 onCopy/onPaste에서 전체 값으로 처리한다.
const SegmentInput = styled.input`
  border: none;
  outline: none;
  background: transparent;
  font-family: var(--font-family);
  font-size: var(--fontSize-14);
  text-align: center;
  width: 2ch;
  padding: 0;

  &::placeholder {
    color: var(--LightNavy);
  }

  &:disabled {
    cursor: not-allowed;
    opacity: 0.6;
  }
`;

// 세그먼트 사이 구분자 ":" (클릭·선택 대상에서 제외)
const Colon = styled.span`
  font-size: var(--fontSize-14);
  padding: 0 1px;
  user-select: none;
  pointer-events: none;
`;

// 포커스가 없을 때 세그먼트 위를 덮는 오버레이. 전체 "HH:MM"이 하나의 텍스트라
// 일반 입력창처럼 드래그 선택/복사가 된다. 드래그 없이 클릭하면 편집 모드로 전환.
const SelectOverlay = styled.div`
  position: absolute;
  inset: 0;
  display: ${({ $hidden }) => ($hidden ? "none" : "flex")};
  align-items: center;
  /* 편집 중 세그먼트는 wrapper 정렬(기본 왼쪽, style로 준 경우 중앙)을 따르므로
     오버레이도 동일하게 상속해 편집 전/후 위치가 튀지 않게 한다. */
  justify-content: inherit;
  padding-left: ${({ $iconPosition }) =>
    $iconPosition === "left" ? "3.5rem" : "1.25rem"};
  padding-right: ${({ $iconPosition }) =>
    $iconPosition === "right" ? "3.5rem" : "1.25rem"};
  background-color: var(--White);
  border-radius: inherit;
  font-family: var(--font-family);
  font-size: var(--fontSize-14);
  color: inherit;
  cursor: text;
  user-select: text;

  &.is-placeholder {
    color: var(--LightNavy);
  }
`;

const getHours = (timeString) => {
  if (!timeString) return null;
  const [hours] = timeString.split(":");
  return Number(hours);
};

const getMinutes = (timeString) => {
  if (!timeString) return null;
  const [, minutes] = timeString.split(":");
  return Number(minutes);
};

const safeMin = (...values) => {
  const filtered = values.filter((v) => v != null);
  return filtered.length ? Math.min(...filtered) : null;
};

const safeMax = (...values) => {
  const filtered = values.filter((v) => v != null);
  return filtered.length ? Math.max(...filtered) : null;
};

// 시간 범위 구분자: "06:00-07:00", "06:00 ~ 07:00" 모두 허용
const RANGE_SEPARATOR = /\s*[-~]\s*/;

// "6:00" / "0600" / "06:00" → "06:00" (시간 형식이 아니면 null)
// 범위를 벗어난 값은 거부하지 않고 클램프한다("25:00" → "23:00").
// (거부하면 preventDefault가 안 걸려 네이티브 붙여넣기가 세그먼트를 망가뜨림)
const normalizeTime = (raw) => {
  const matched = String(raw ?? "")
    .trim()
    .match(/^(\d{1,2}):?(\d{2})$/);
  if (!matched) return null;
  const h = Math.min(23, Number(matched[1]));
  const m = Math.min(59, Number(matched[2]));
  return `${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}`;
};

export const TimeInput = ({
  label,
  caption,
  icon,
  rules,
  name,
  disabled,
  readOnly,
  defaultValue,
  onChange,
  forceError,
  minTime,
  maxTime,
  style,
  borderless,
  copyText,
  onPasteRange,
  ...rest
}) => {
  const hourRef = useRef(null);
  const minuteRef = useRef(null);
  const dragOrigin = useRef(null);

  // 편집 중이면 세그먼트, 아니면 선택 가능한 오버레이를 보여준다.
  const [isEditing, setIsEditing] = useState(false);

  const { inputProps, error, onValueChange } = useValidCheckSingle({
    name,
    rules: {
      ...rules,
      validate: {
        ...rules?.validate,
        completeTime: (value) => {
          if (!value) return !rules?.required;
          return TIME_PATTERN.test(value);
        },
      },
    },
    defaultValue,
  });

  const hasError = forceError || error;

  const maxHour = safeMin(23, maxTime && getHours(maxTime));
  const minHour = safeMax(0, minTime && getHours(minTime));

  const value = disabled ? "" : (inputProps.value ?? "");

  // 분 단위 min/max는 시(hour)가 경계값과 같을 때만 적용
  const getMaxMinute = (h) =>
    maxTime && h === getHours(maxTime) ? getMinutes(maxTime) : 59;
  const getMinMinute = (h) =>
    minTime && h === getHours(minTime) ? getMinutes(minTime) : 0;

  // 숫자만 남긴 문자열(최대 4자리) → "HH:MM" 표시값
  const formatDigits = (digits) =>
    digits.length > 2 ? `${digits.slice(0, 2)}:${digits.slice(2)}` : digits;

  // 입력 도중에도 시/분을 min·max 범위로 보정
  const clampDigits = (raw) => {
    let digits = raw;
    if (digits.length >= 2) {
      let h = Number(digits.slice(0, 2));
      if (maxHour != null && h > maxHour) h = maxHour;
      if (minHour != null && h < minHour) h = minHour;
      digits = String(h).padStart(2, "0") + digits.slice(2);
    }
    if (digits.length === 4) {
      const h = Number(digits.slice(0, 2));
      let m = Number(digits.slice(2));
      m = Math.min(getMaxMinute(h), Math.max(getMinMinute(h), m));
      digits = digits.slice(0, 2) + String(m).padStart(2, "0");
    }
    return digits;
  };

  const commit = (next) => {
    onValueChange(next);
    onChange?.(next);
  };

  // 현재 값을 시/분 세그먼트로 분리 ("6" → h:"6" m:"", "06:3" → h:"06" m:"3")
  const colonIdx = value.indexOf(":");
  const hStr = colonIdx >= 0 ? value.slice(0, colonIdx) : value;
  const mStr = colonIdx >= 0 ? value.slice(colonIdx + 1) : "";

  const clampHour = (h) => {
    if (maxHour != null && h > maxHour) h = maxHour;
    if (minHour != null && h < minHour) h = minHour;
    return h;
  };
  const clampMinute = (h, m) =>
    Math.min(getMaxMinute(h), Math.max(getMinMinute(h), m));

  // 클릭할 때마다 시↔분 세그먼트로 포커스를 토글한다. mousedown에서 네이티브 포커스
  // 이동을 막아야(클릭한 세그먼트로 바로 가지 않도록) 클릭 위치와 무관하게 토글된다.
  // 포커스된 세그먼트가 없으면 시부터 시작한다.
  const handleWrapperMouseDown = (e) => {
    if (disabled || readOnly) return;
    // 오버레이(비편집) 상태에선 드래그 선택을 막지 않도록 여기서 관여하지 않는다.
    if (!isEditing) return;
    e.preventDefault();
    const active = document.activeElement;
    const target =
      active === hourRef.current ? minuteRef.current : hourRef.current;
    target?.focus();
  };

  // 포커스 시 세그먼트 전체 선택 → 바로 덮어쓰기 가능
  const handleFocus = (e) => {
    setIsEditing(true);
    e.target.select();
  };

  // 오버레이 mousedown: preventDefault를 걸지 않아 네이티브 텍스트 선택을 허용한다.
  const handleOverlayMouseDown = (e) => {
    if (disabled) return;
    dragOrigin.current = { x: e.clientX, y: e.clientY };
  };

  // 드래그 없이 클릭했으면 편집 모드로 전환, 드래그(선택)면 선택을 유지한다.
  const handleOverlayMouseUp = (e) => {
    if (disabled || readOnly) return;
    const origin = dragOrigin.current;
    dragOrigin.current = null;
    const moved =
      origin &&
      (Math.abs(e.clientX - origin.x) > 3 || Math.abs(e.clientY - origin.y) > 3);
    const sel = window.getSelection?.();
    const hasSelection = sel && !sel.isCollapsed && sel.toString().trim();
    if (moved || hasSelection) return;
    setIsEditing(true);
    hourRef.current?.focus();
  };

  // 시 세그먼트: 2자리 입력되면 보정 후 분으로 자동 이동
  const handleHourChange = (e) => {
    if (disabled || readOnly) return;
    let digits = e.target.value.replace(/\D/g, "").slice(0, 2);
    if (digits.length === 2)
      digits = String(clampHour(Number(digits))).padStart(2, "0");
    const next = digits.length === 2 || mStr !== "" ? `${digits}:${mStr}` : digits;
    commit(next);
    if (digits.length === 2) minuteRef.current?.focus();
  };

  // 분 세그먼트
  const handleMinuteChange = (e) => {
    if (disabled || readOnly) return;
    let digits = e.target.value.replace(/\D/g, "").slice(0, 2);
    if (digits.length === 2) {
      const h = Number(hStr) || 0;
      digits = String(clampMinute(h, Number(digits))).padStart(2, "0");
    }
    commit(`${hStr}:${digits}`);
  };

  const stepHour = (delta) => {
    const h = clampHour((Number(hStr) || 0) + delta);
    commit(`${String(h).padStart(2, "0")}:${mStr}`);
  };
  const stepMinute = (delta) => {
    const h = Number(hStr) || 0;
    const m = clampMinute(h, (Number(mStr) || 0) + delta);
    commit(`${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}`);
  };

  const handleHourKeyDown = (e) => {
    if (disabled || readOnly) return;
    if (e.key === "ArrowUp") {
      e.preventDefault();
      stepHour(1);
    } else if (e.key === "ArrowDown") {
      e.preventDefault();
      stepHour(-1);
    } else if (
      (e.key === ":" || e.key === "ArrowRight") &&
      (e.target.selectionStart ?? 0) >= hStr.length
    ) {
      // 오른쪽 끝에서 → 분으로 이동
      if (e.key === ":") e.preventDefault();
      minuteRef.current?.focus();
    }
  };

  const handleMinuteKeyDown = (e) => {
    if (disabled || readOnly) return;
    if (e.key === "ArrowUp") {
      e.preventDefault();
      stepMinute(1);
    } else if (e.key === "ArrowDown") {
      e.preventDefault();
      stepMinute(-1);
    } else if (
      (e.key === "ArrowLeft" ||
        (e.key === "Backspace" && mStr === "")) &&
      (e.target.selectionStart ?? 0) === 0
    ) {
      // 커서 맨 앞 왼쪽 이동 / 분이 빈 채 백스페이스 → 시로 이동
      hourRef.current?.focus();
    }
  };

  // 포커스가 세그먼트를 떠나면 그 세그먼트를 2자리로 채운다("6"→"06").
  // 시↔분 이동에도 적용되도록 아래 조기 return보다 먼저 처리한다.
  const handleSegmentBlur = (e) => {
    // 자동 이동(2자리 입력)과의 경쟁을 피하려 클로저(hStr/mStr) 대신
    // DOM의 실제 입력값을 기준으로 판단한다.
    const raw = e.target.value.replace(/\D/g, "");
    if (e.target === hourRef.current && raw !== "" && raw.length < 2) {
      const h = clampHour(Number(raw));
      commit(`${String(h).padStart(2, "0")}:${mStr}`);
    } else if (e.target === minuteRef.current && raw !== "" && raw.length < 2) {
      const m = clampMinute(Number(hStr) || 0, Number(raw));
      commit(`${hStr}:${String(m).padStart(2, "0")}`);
    }

    if (
      e.relatedTarget === hourRef.current ||
      e.relatedTarget === minuteRef.current
    )
      return;
    // 두 세그먼트 밖으로 나가면 "HH:MM"으로 정규화
    const digits = value.replace(/\D/g, "");
    if (digits.length === 4) commit(formatDigits(clampDigits(digits)));
    inputProps.onBlur();
    setIsEditing(false);
  };

  // 세그먼트는 포커스 시 전체 선택되므로, 선택 여부로 분기하면 시/분 한쪽만
  // 복사된다. 항상 전체 값(copyText 있으면 행 전체 범위, 없으면 "HH:MM")을 복사한다.
  const handleCopy = (e) => {
    // 오버레이에서 직접 드래그 선택한 경우(세그먼트에 포커스가 없고 실제 선택이 있음)엔
    // 선택한 그대로 네이티브 복사한다.
    const inSegment =
      document.activeElement === hourRef.current ||
      document.activeElement === minuteRef.current;
    if (!inSegment) {
      const sel = window.getSelection?.();
      if (sel && !sel.isCollapsed && sel.toString().trim()) return;
    }
    const text = copyText || value || "";
    if (!text) return;
    e.clipboardData.setData("text/plain", text);
    e.preventDefault();
  };

  // "06:00-07:00" 범위면 onPasteRange로 위임, 단일 "06:00"이면 이 필드에 적용
  const handlePaste = (e) => {
    if (disabled || readOnly) return;
    const text = e.clipboardData.getData("text");
    if (!text) return;

    const parts = text.split(RANGE_SEPARATOR);
    if (parts.length >= 2 && onPasteRange) {
      const start = normalizeTime(parts[0]);
      const end = normalizeTime(parts[1]);
      if (start && end) {
        e.preventDefault();
        onPasteRange(start, end);
        return;
      }
    }

    const single = normalizeTime(parts[0]);
    if (single) {
      e.preventDefault();
      onValueChange(single);
      onChange?.(single);
    }
  };

  return (
    <InputWrapper
      label={label}
      caption={caption}
      icon={icon}
      rules={rules}
      error={hasError}>
      <TimeInputWrapper
        iconPosition={icon?.position}
        $error={!!hasError}
        $disabled={disabled}
        $borderless={borderless}
        style={style}
        data-field={name}
        onMouseDown={handleWrapperMouseDown}
        onCopy={handleCopy}
        onPaste={handlePaste}
        {...rest}>
        <SegmentInput
          ref={(el) => {
            hourRef.current = el;
            // 폼(react-hook-form) ref는 시 세그먼트에 연결 (focus/validation 대상)
            if (typeof inputProps.ref === "function") inputProps.ref(el);
            else if (inputProps.ref) inputProps.ref.current = el;
          }}
          type="text"
          inputMode="numeric"
          value={hStr}
          placeholder="--"
          aria-label="hour"
          onFocus={handleFocus}
          onChange={handleHourChange}
          onKeyDown={handleHourKeyDown}
          onBlur={handleSegmentBlur}
          disabled={disabled}
          readOnly={readOnly}
          maxLength={2}
        />
        <Colon>:</Colon>
        <SegmentInput
          ref={minuteRef}
          type="text"
          inputMode="numeric"
          value={mStr}
          placeholder="--"
          aria-label="minute"
          onFocus={handleFocus}
          onChange={handleMinuteChange}
          onKeyDown={handleMinuteKeyDown}
          onBlur={handleSegmentBlur}
          disabled={disabled}
          readOnly={readOnly}
          maxLength={2}
        />
        {!disabled && (
          <SelectOverlay
            $hidden={isEditing}
            $iconPosition={icon?.position}
            className={value ? undefined : "is-placeholder"}
            onMouseDown={handleOverlayMouseDown}
            onMouseUp={handleOverlayMouseUp}>
            {value || "--:--"}
          </SelectOverlay>
        )}
      </TimeInputWrapper>
    </InputWrapper>
  );
};
